import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tabs,
  Timeline,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AuditOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  RocketOutlined,
  ScheduleOutlined,
  SearchOutlined,
  StarOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '../../../lib/toast';
import { useContactKundali } from '../hooks/useContactKundali';
import { useContact } from '../hooks/useContact';
import { normalizeHttpUrl } from '../../../lib/url';
import { useUpdateContact } from '../hooks/useUpdateContact';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useContactCategories } from '../hooks/useContactCategories';
import { useContactRoles } from '../hooks/useContactRoles';
import ContactFormDrawer from './ContactFormDrawer';
import LeadViewDrawer from '../../leads/components/LeadViewDrawer';
import { DocumentDetailsDrawer } from '../../document-management/components/DocumentDetailsDrawer';
import { useDocument } from '../../document-management/hooks/useDocument';
import PageScaffold from '../../../components/PageScaffold';
import Can from '../../permissions/Can';
import {
  PIPELINE_STAGE_LABELS,
  CLOSING_STATUS_LABELS,
  getBantTier,
} from '../../leads/constants';
import type { KundaliLeadItem, KundaliFollowUpItem, KundaliQuoteItem, KundaliActivityItem, ContactFormValues } from '../types';
import { contactTypeLabel, isOrganizationContactType } from '../contactType';

const { Title, Text, Paragraph } = Typography;

const QUOTE_STATUS_LABELS: Record<number, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'Accepted',
  3: 'Rejected',
};

const QUOTE_STATUS_COLORS: Record<number, string> = {
  0: 'default',
  1: 'blue',
  2: 'green',
  3: 'red',
};

const STAGE_COLORS: Record<number, string> = {
  1: 'default',
  2: 'cyan',
  3: 'geekblue',
  4: 'blue',
  5: 'orange',
  6: 'gold',
  7: 'purple',
};

const CLOSING_COLORS: Record<number, string> = {
  0: 'default',
  1: 'success',
  2: 'error',
  3: 'warning',
};

const ACTIVITY_TYPE_LABELS: Record<number, string> = {
  1: 'Phone Call',
  2: 'Email',
  3: 'Social Media',
  4: 'Meeting',
  5: 'Event',
  6: 'Internal Note',
  8: 'Walk-in',
  9: 'Other',
  10: 'Follow-up',
};

const ACTIVITY_TYPE_COLORS: Record<number, string> = {
  1: '#1677ff',
  2: '#722ed1',
  3: '#13c2c2',
  4: '#52c41a',
  5: '#fa8c16',
  6: '#8c8c8c',
  8: '#13c2c2',
  9: '#8c8c8c',
  10: '#2f54eb',
};

function ActivityTypeIcon({ type }: { type: number }) {
  switch (type) {
    case 1: return <PhoneOutlined />;
    case 2: return <MailOutlined />;
    case 3: return <MessageOutlined />;
    case 4: return <VideoCameraOutlined />;
    case 5: return <RocketOutlined />;
    case 6: return <AuditOutlined />;
    case 8: return <TeamOutlined />;
    case 9: return <AuditOutlined />;
    case 10: return <ScheduleOutlined />;
    default: return <AuditOutlined />;
  }
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return s ?? '—';
  }
}

function formatDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s ?? '—';
  }
}

function formatCurrency(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return String(val);
}

function getInitials(name: string | null, company: string | null): string {
  const display = name ?? company ?? '?';
  const parts = display.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return display.slice(0, 2).toUpperCase();
}

function copyToClipboard(text: string) {
  void navigator.clipboard.writeText(text).then(() => {
    void toast.success('Copied!');
  });
}

function formatPhoneForTel(phone: string): string {
  const cleaned = phone.trim().replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return `+${cleaned.slice(1).replace(/\+/g, '')}`;
  return cleaned.replace(/\+/g, '');
}

const KundaliSearchPrompt: React.FC = () => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        gap: 16,
      }}
    >
      <StarOutlined style={{ fontSize: 48, color: token.colorPrimary, opacity: 0.4 }} />
      <Title level={3} style={{ margin: 0 }}>
        Contact Kundali
      </Title>
      <Text type="secondary" style={{ fontSize: 15, textAlign: 'center', maxWidth: 420 }}>
        Search for any contact and instantly see their complete history — leads, quotes, follow-ups and revenue — all in one place.
      </Text>
      <Button
        type="primary"
        size="large"
        icon={<SearchOutlined />}
        onClick={() => window.dispatchEvent(new Event('open-command-search'))}
      >
        Search Contact&nbsp;&nbsp;<kbd style={{ fontSize: 11, opacity: 0.8, background: 'rgba(255,255,255,0.2)', borderRadius: 3, padding: '1px 5px' }}>Ctrl+K</kbd>
      </Button>
    </div>
  );
};

const KundaliPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);

  const { data: kundali, isLoading, isError } = useContactKundali(id ?? null);
  const { data: contactDetail, isLoading: loadingContact } = useContact(editOpen ? (id ?? null) : null);
  const { data: users = [], isLoading: loadingUsers } = useUserOptions();
  const { data: categoriesData, isLoading: loadingCategories } = useContactCategories();
  const { data: rolesData, isLoading: loadingRoles } = useContactRoles();
  const { data: selectedQuote, isLoading: loadingQuote } = useDocument(selectedQuoteId);

  const ownerOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.label })),
    [users]
  );
  const categoryOptions = useMemo(
    () =>
      (categoriesData ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.id, label: c.label })),
    [categoriesData]
  );
  const roleOptions = rolesData ?? [];

  const { mutate: updateContact, isPending: saving } = useUpdateContact(id ?? null, () => {
    setEditOpen(false);
    void queryClient.invalidateQueries({ queryKey: ['contactKundali', id] });
  });

  const handleSave = (values: ContactFormValues) => {
    updateContact(values);
  };

  if (!id) {
    return (
      <PageScaffold hideChrome>
        <KundaliSearchPrompt />
      </PageScaffold>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0 0 24px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
        <Row gutter={16} style={{ marginTop: 16 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Col key={i} xs={12} sm={8} md={4}>
              <Skeleton.Button active block style={{ height: 80 }} />
            </Col>
          ))}
        </Row>
        <Skeleton active style={{ marginTop: 16 }} paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (isError || !kundali) {
    return (
      <Empty
        description="Contact not found"
        style={{ marginTop: 80 }}
      >
        <Button onClick={() => navigate('/kundali')}>Back to Kundali</Button>
      </Empty>
    );
  }

  const { leadStats, quoteStats } = kundali;
  const displayName = kundali.companyName ?? kundali.name ?? 'Unknown';
  const personName = kundali.name && kundali.companyName ? kundali.name : null;

  const isOverdue = (dateStr: string | null | undefined) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // ---- Leads tab columns ----
  const leadColumns: ColumnsType<KundaliLeadItem> = [
    {
      title: 'Lead',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => <Text strong style={{ fontSize: 13 }}>{title}</Text>,
    },
    {
      title: 'Stage',
      dataIndex: 'pipelineStage',
      key: 'stage',
      width: 130,
      render: (stage: number) => (
        <Tag color={STAGE_COLORS[stage] ?? 'default'} style={{ fontSize: 11 }}>
          {PIPELINE_STAGE_LABELS[stage as keyof typeof PIPELINE_STAGE_LABELS] ?? stage}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'closingStatus',
      key: 'status',
      width: 110,
      render: (status: number) =>
        status !== 0 ? (
          <Tag color={CLOSING_COLORS[status] ?? 'default'} style={{ fontSize: 11 }}>
            {CLOSING_STATUS_LABELS[status as keyof typeof CLOSING_STATUS_LABELS] ?? status}
          </Tag>
        ) : (
          <Tag color="default" style={{ fontSize: 11 }}>
            Open
          </Tag>
        ),
    },
    {
      title: 'BANT',
      dataIndex: 'bantTotalScore',
      key: 'bant',
      width: 80,
      render: (score: number) => {
        const tier = getBantTier(score);
        return <Badge status={tier.color === 'default' ? 'default' : tier.color === 'warning' ? 'warning' : tier.color === 'processing' ? 'processing' : 'success'} text={`${score}/20`} />;
      },
    },
    {
      title: 'Target',
      dataIndex: 'targetedAmount',
      key: 'target',
      width: 90,
      align: 'right',
      render: (val: number) => <Text style={{ fontSize: 13 }}>{formatCurrency(val)}</Text>,
    },
    {
      title: 'Closed',
      dataIndex: 'closedAmount',
      key: 'closed',
      width: 90,
      align: 'right',
      render: (val: number | null) =>
        val != null ? <Text type="success" style={{ fontSize: 13 }}>{formatCurrency(val)}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Completed',
      dataIndex: 'actualClosingDate',
      key: 'completed',
      width: 110,
      render: (date: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {date ? formatDate(date) : '—'}
        </Text>
      ),
    },
  ];

  // ---- Documents tab columns ----
  const quoteColumns: ColumnsType<KundaliQuoteItem> = [
    {
      title: 'Document',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => <Text strong style={{ fontSize: 13 }}>{title}</Text>,
    },
    {
      title: 'Lead',
      dataIndex: 'leadTitle',
      key: 'lead',
      render: (lead: string) => <Text type="secondary" style={{ fontSize: 12 }}>{lead}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => (
        <Tag color={QUOTE_STATUS_COLORS[status] ?? 'default'} style={{ fontSize: 11 }}>
          {QUOTE_STATUS_LABELS[status] ?? status}
        </Tag>
      ),
    },
    {
      title: 'Sent',
      dataIndex: 'sentAtUtc',
      key: 'sent',
      width: 110,
      render: (date: string | null) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(date)}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAtUtc',
      key: 'created',
      width: 110,
      render: (date: string) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(date)}</Text>,
    },
  ];

  return (
    <div>
      {/* ---- HEADER CARD ---- */}
      <Card
        style={{ marginBottom: 16, borderRadius: 10 }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Avatar
              size={56}
              icon={isOrganizationContactType(kundali.contactType) ? <BankOutlined /> : <UserOutlined />}
              style={{
                backgroundColor: isOrganizationContactType(kundali.contactType) ? '#e6f4ff' : '#f6ffed',
                color: isOrganizationContactType(kundali.contactType) ? '#1677ff' : '#52c41a',
                fontSize: 22,
              }}
            >
              {getInitials(kundali.name, kundali.companyName)}
            </Avatar>
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Title level={4} style={{ margin: 0 }}>
                {displayName}
              </Title>
              <Tag color={isOrganizationContactType(kundali.contactType) ? 'blue' : 'green'} style={{ fontSize: 11 }}>
                {contactTypeLabel(kundali.contactType)}
              </Tag>
              <Tag color="purple" style={{ fontSize: 11 }}>
                {kundali.categoryLabel}
              </Tag>
              {kundali.tags.map((tag) => (
                <Tag key={tag} style={{ fontSize: 11 }}>{tag}</Tag>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
              {personName && (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <UserOutlined style={{ marginRight: 4 }} />{personName}
                </Text>
              )}
              {kundali.jobTitle && (
                <Text type="secondary" style={{ fontSize: 13 }}>{kundali.jobTitle}</Text>
              )}
              {kundali.email && (
                <Space size={4}>
                  <MailOutlined style={{ fontSize: 12 }} />
                  <a href={`mailto:${kundali.email}`}>
                    <Text style={{ fontSize: 13 }}>{kundali.email}</Text>
                  </a>
                  <Tooltip title="Copy email">
                    <CopyOutlined
                      style={{ fontSize: 11, cursor: 'pointer', opacity: 0.5 }}
                      onClick={() => copyToClipboard(kundali.email!)}
                    />
                  </Tooltip>
                </Space>
              )}
              {kundali.mobileNumber && (
                <Space size={4}>
                  <PhoneOutlined style={{ fontSize: 12 }} />
                  <a href={`tel:${formatPhoneForTel(kundali.mobileNumber)}`}>
                    <Text style={{ fontSize: 13 }}>{kundali.mobileNumber}</Text>
                  </a>
                  <Tooltip title="Copy phone">
                    <CopyOutlined
                      style={{ fontSize: 11, cursor: 'pointer', opacity: 0.5 }}
                      onClick={() => copyToClipboard(kundali.mobileNumber!)}
                    />
                  </Tooltip>
                </Space>
              )}
            </div>
          </Col>
          <Col flex="none">
            <Space>
              <Tooltip title="Use Ctrl+K to search another contact">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => window.dispatchEvent(new Event('open-command-search'))}
                >
                  Search
                </Button>
              </Tooltip>
              <Can permission="contacts.edit">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setEditOpen(true)}
                >
                  Edit
                </Button>
              </Can>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ---- STATS ROW ---- */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Total Leads</span>}
              value={leadStats.totalLeads}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Active Leads</span>}
              value={leadStats.activeLeads}
              valueStyle={{ color: '#1677ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Deals Won</span>}
              value={leadStats.wonLeads}
              valueStyle={{ color: '#52c41a' }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Revenue Closed</span>}
              value={leadStats.totalClosedAmount}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Documents Sent</span>}
              value={quoteStats.sentQuotes + quoteStats.acceptedQuotes + quoteStats.rejectedQuotes}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card styles={{ body: { padding: '16px 20px' } }} style={{ borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>Documents Accepted</span>}
              value={quoteStats.acceptedQuotes}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* ---- MAIN CONTENT + SIDEBAR ---- */}
      <Row gutter={16}>
        {/* Main tabs */}
        <Col xs={24} lg={17}>
          <Card style={{ borderRadius: 8 }} styles={{ body: { padding: '0 0 4px' } }}>
            <Tabs
              defaultActiveKey="leads"
              style={{ padding: '0 16px' }}
              items={[
                {
                  key: 'leads',
                  label: (
                    <span>
                      <TeamOutlined /> Leads{' '}
                      {leadStats.totalLeads > 0 && (
                        <Tag color="blue" style={{ fontSize: 10, marginLeft: 4 }}>{leadStats.totalLeads}</Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <Table<KundaliLeadItem>
                      dataSource={kundali.recentLeads}
                      columns={leadColumns}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText: <Empty description="No leads yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      onRow={(record) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => setSelectedLeadId(record.id),
                      })}
                      style={{ marginTop: 8 }}
                    />
                  ),
                },
                {
                  key: 'documents',
                  label: (
                    <span>
                      <FileTextOutlined /> Documents{' '}
                      {quoteStats.totalQuotes > 0 && (
                        <Tag color="purple" style={{ fontSize: 10, marginLeft: 4 }}>{quoteStats.totalQuotes}</Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <div>
                      {quoteStats.totalQuotes > 0 && (
                        <Row gutter={8} style={{ marginTop: 8, marginBottom: 12 }}>
                          {quoteStats.draftQuotes > 0 && (
                            <Col><Tag>{quoteStats.draftQuotes} Draft</Tag></Col>
                          )}
                          {quoteStats.sentQuotes > 0 && (
                            <Col><Tag color="blue">{quoteStats.sentQuotes} Sent</Tag></Col>
                          )}
                          {quoteStats.acceptedQuotes > 0 && (
                            <Col><Tag color="green">{quoteStats.acceptedQuotes} Accepted</Tag></Col>
                          )}
                          {quoteStats.rejectedQuotes > 0 && (
                            <Col><Tag color="red">{quoteStats.rejectedQuotes} Rejected</Tag></Col>
                          )}
                        </Row>
                      )}
                      <Table<KundaliQuoteItem>
                        dataSource={kundali.recentQuotes}
                        columns={quoteColumns}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        locale={{ emptyText: <Empty description="No documents yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                        onRow={(record) => ({
                          style: { cursor: 'pointer' },
                          onClick: () => setSelectedQuoteId(record.id),
                        })}
                      />
                    </div>
                  ),
                },
                {
                  key: 'followups',
                  label: (
                    <span>
                      <CalendarOutlined /> Follow-ups{' '}
                      {kundali.upcomingFollowUps.length > 0 && (
                        <Tag
                          color={kundali.upcomingFollowUps.some((f) => isOverdue(f.dueDate)) ? 'red' : 'orange'}
                          style={{ fontSize: 10, marginLeft: 4 }}
                        >
                          {kundali.upcomingFollowUps.length}
                        </Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <div style={{ marginTop: 8 }}>
                      {kundali.upcomingFollowUps.length === 0 ? (
                        <Empty description="No pending follow-ups" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      ) : (
                        kundali.upcomingFollowUps.map((f: KundaliFollowUpItem) => {
                          const overdue = isOverdue(f.dueDate);
                          return (
                            <Card
                              key={f.taskId}
                              size="small"
                              style={{
                                marginBottom: 8,
                                cursor: f.leadId ? 'pointer' : 'default',
                                borderLeft: `3px solid ${overdue ? '#ff4d4f' : '#faad14'}`,
                              }}
                              styles={{ body: { padding: '10px 14px' } }}
                              onClick={() => f.leadId && setSelectedLeadId(f.leadId)}
                            >
                              <Row align="middle" justify="space-between">
                                <Col>
                                  <Text strong style={{ fontSize: 13 }}>{f.taskTitle}</Text>
                                  <div style={{ marginTop: 2 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{f.leadTitle}</Text>
                                  </div>
                                  <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {f.pipelineStage !== null && (
                                      <Tag
                                        color={STAGE_COLORS[f.pipelineStage] ?? 'default'}
                                        style={{ fontSize: 11 }}
                                      >
                                        {PIPELINE_STAGE_LABELS[f.pipelineStage as keyof typeof PIPELINE_STAGE_LABELS]}
                                      </Tag>
                                    )}
                                  </div>
                                </Col>
                                <Col>
                                  <Text
                                    type={overdue ? 'danger' : 'warning'}
                                    style={{ fontSize: 13, fontWeight: 500 }}
                                  >
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    {formatDate(f.dueDate)}
                                  </Text>
                                  {overdue && (
                                    <div>
                                      <Tag color="red" style={{ fontSize: 10 }}>Overdue</Tag>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  ),
                },
                {
                  key: 'activities',
                  label: (
                    <span>
                      <AuditOutlined /> Activities{' '}
                      {kundali.recentActivities.length > 0 && (
                        <Tag color="geekblue" style={{ fontSize: 10, marginLeft: 4 }}>
                          {kundali.recentActivities.length}
                        </Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <div style={{ marginTop: 16, paddingBottom: 8 }}>
                      {kundali.recentActivities.length === 0 ? (
                        <Empty description="No activities recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      ) : (
                        <Timeline
                          items={kundali.recentActivities.map((a: KundaliActivityItem) => ({
                            dot: (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  background: `${ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c'}18`,
                                  color: ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c',
                                  fontSize: 13,
                                }}
                              >
                                <ActivityTypeIcon type={a.activityType} />
                              </span>
                            ),
                            color: ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c',
                            children: (
                              <div
                                style={{ cursor: 'pointer', paddingBottom: 4 }}
                                onClick={() => a.leadId && setSelectedLeadId(a.leadId)}
                              >
                                {/* Row 1: type tag + lead + stage + datetime */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <Tag
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: `${ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c'}18`,
                                      color: ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c',
                                      border: `1px solid ${ACTIVITY_TYPE_COLORS[a.activityType] ?? '#8c8c8c'}40`,
                                      margin: 0,
                                    }}
                                  >
                                    {ACTIVITY_TYPE_LABELS[a.activityType] ?? 'Activity'}
                                  </Tag>
                                  <Text
                                    strong
                                    style={{ fontSize: 13 }}
                                    onClick={(e) => { e.stopPropagation(); if (a.leadId) setSelectedLeadId(a.leadId); }}
                                  >
                                    {a.leadTitle}
                                  </Text>
                                  {a.pipelineStageSnapshot !== null && (
                                    <Tag
                                      color={STAGE_COLORS[a.pipelineStageSnapshot] ?? 'default'}
                                      style={{ fontSize: 10, margin: 0 }}
                                    >
                                      {PIPELINE_STAGE_LABELS[a.pipelineStageSnapshot as keyof typeof PIPELINE_STAGE_LABELS] ?? `Stage ${a.pipelineStageSnapshot}`}
                                    </Tag>
                                  )}
                                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    {formatDateTime(a.date)}
                                  </Text>
                                </div>
                                {/* Row 2: note */}
                                {a.note && (
                                  <Text style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                                    {a.note}
                                  </Text>
                                )}
                                {/* Row 3: created by */}
                                {a.createdByDisplayName && (
                                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <UserOutlined style={{ fontSize: 11, opacity: 0.45 }} />
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                      {a.createdByDisplayName}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            ),
                          }))}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* Right sidebar — Contact Info */}
        <Col xs={24} lg={7}>
          <Card
            title="Contact Info"
            size="small"
            style={{ borderRadius: 8, marginBottom: 12 }}
            extra={
              <Can permission="contacts.edit">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setEditOpen(true)}
                />
              </Can>
            }
          >
            <Descriptions column={1} size="small" styles={{ label: { fontSize: 12, width: 90 }, content: { fontSize: 12 } }}>
              {kundali.email && (
                <Descriptions.Item label={<Space size={5}><MailOutlined />Email</Space>}>
                  <Space size={4}>
                    <a href={`mailto:${kundali.email}`}>{kundali.email}</a>
                    <CopyOutlined
                      style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                      onClick={() => copyToClipboard(kundali.email!)}
                    />
                  </Space>
                </Descriptions.Item>
              )}
              {kundali.mobileNumber && (
                <Descriptions.Item label={<Space size={5}><PhoneOutlined />Phone</Space>}>
                  <Space size={4}>
                    <a href={`tel:${kundali.mobileNumber}`}>{kundali.mobileNumber}</a>
                    <CopyOutlined
                      style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                      onClick={() => copyToClipboard(kundali.mobileNumber!)}
                    />
                  </Space>
                </Descriptions.Item>
              )}
              {kundali.whatsAppNumber && (
                <Descriptions.Item label={<Space size={5}><WhatsAppOutlined />WhatsApp</Space>}>
                  {kundali.whatsAppNumber}
                </Descriptions.Item>
              )}
              {kundali.website && (
                <Descriptions.Item label={<Space size={5}><GlobalOutlined />Website</Space>}>
                  <a href={normalizeHttpUrl(kundali.website) ?? '#'} target="_blank" rel="noreferrer">{kundali.website}</a>
                </Descriptions.Item>
              )}
              {kundali.linkedIn && (
                <Descriptions.Item label={<Space size={5}><LinkedinOutlined />LinkedIn</Space>}>
                  <a href={kundali.linkedIn} target="_blank" rel="noreferrer">Profile</a>
                </Descriptions.Item>
              )}
              {kundali.address && (
                <Descriptions.Item label={<Space size={5}><EnvironmentOutlined />Address</Space>}>
                  {kundali.googleMapsLink ? (
                    <a href={kundali.googleMapsLink} target="_blank" rel="noreferrer">{kundali.address}</a>
                  ) : (
                    kundali.address
                  )}
                </Descriptions.Item>
              )}
              {kundali.ownerDisplayName && (
                <Descriptions.Item label={<Space size={5}><UserOutlined />Owner</Space>}>
                  {kundali.ownerDisplayName}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={<Space size={5}><BankOutlined />Category</Space>}>
                {kundali.categoryLabel}
              </Descriptions.Item>
              <Descriptions.Item label="Since">
                {formatDate(kundali.createdAtUtc)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {kundali.notes && (
            <Card title="Notes" size="small" style={{ borderRadius: 8, marginBottom: 12 }}>
              <Paragraph style={{ fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>
                {kundali.notes}
              </Paragraph>
            </Card>
          )}

          {/* Pipeline breakdown mini-card */}
          {leadStats.totalLeads > 0 && (
            <Card title="Pipeline Snapshot" size="small" style={{ borderRadius: 8 }}>
              {Object.entries(leadStats.pipelineBreakdown)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([stage, count]) => (
                  <Row key={stage} justify="space-between" align="middle" style={{ marginBottom: 6 }}>
                    <Tag color={STAGE_COLORS[Number(stage)] ?? 'default'} style={{ fontSize: 11, margin: 0 }}>
                      {PIPELINE_STAGE_LABELS[Number(stage) as keyof typeof PIPELINE_STAGE_LABELS] ?? `Stage ${stage}`}
                    </Tag>
                    <Text strong style={{ fontSize: 13 }}>{count as number}</Text>
                  </Row>
                ))}
              <Row justify="space-between" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Targeted</Text>
                <Text strong style={{ fontSize: 13 }}>{formatCurrency(leadStats.totalTargetedAmount)}</Text>
              </Row>
              <Row justify="space-between" style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Deals Lost</Text>
                <Text type="danger" style={{ fontSize: 13 }}>{leadStats.lostLeads}</Text>
              </Row>
            </Card>
          )}
        </Col>
      </Row>

      {/* ---- DRAWERS ---- */}
      <ContactFormDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        mode="edit"
        contactId={id ?? null}
        contact={contactDetail ?? null}
        loadingContact={loadingContact}
        ownerOptions={ownerOptions}
        categoryOptions={categoryOptions}
        roleOptions={roleOptions}
        loadingCategories={loadingCategories}
        loadingRoles={loadingRoles}
        loadingOwners={loadingUsers}
        onSave={handleSave}
        saving={saving}
      />

      <LeadViewDrawer
        open={selectedLeadId !== null}
        onClose={() => setSelectedLeadId(null)}
        leadId={selectedLeadId}
      />

      <DocumentDetailsDrawer
        open={selectedQuoteId !== null}
        onClose={() => setSelectedQuoteId(null)}
        quote={selectedQuote}
        loading={loadingQuote}
        onUpdated={() => {
          void queryClient.invalidateQueries({ queryKey: ['contactKundali', id] });
          void queryClient.invalidateQueries({ queryKey: ['documents'] });
        }}
      />
    </div>
  );
};

export default KundaliPage;
